import dayjs from 'dayjs';

import {
  ClientStatusEnum,
  ClientStatusLabelEnum,
} from '@app/components/RecordModal/components/RecordDetails/types';
import { formatStringDateToDayjs, formatDayjsToString } from '@app/utils';

import type {
  GenderItem,
  HasRecordsItem,
  NewClientsListFilters,
  NewClientsListFiltersForLocalStorage,
  RecordsStatusItem,
  RecordsTagsItem,
  Segment,
} from '@pages/Clients/NewClientsList/types';

import type { MenuProps } from 'antd';

export const PF = 'NewClientsListPage';

export const INITIAL_FILTERS: NewClientsListFilters = {
  id: 1,
  type: 'and',
  hasRecords: null,
  recordsStatuses: [],
  recordsCountRange: { from: null, to: null },
  servicesSumRange: { from: null, to: null },
  recordsTags: [],
  isIncludeRecordsTags: true,
  period: null,
  employees: [],
  categories: [],
  services: [],
  genders: [],
  clientsTags: [],
  isIncludeClientsTags: true,
  soldRange: { from: null, to: null },
  birthdayPeriod: null,
  ageRange: { from: null, to: null },
  balanceRange: { from: null, to: null },
  hasTasks: null,
  hasLoyaltyCard: null,
};

export const createInitialFilters = (): NewClientsListFilters[] => {
  const filtersFromLocalStorage = sessionStorage.getItem(
    'NewClientsListFilters'
  );

  if (filtersFromLocalStorage) {
    const parsedFilters: NewClientsListFiltersForLocalStorage[] = JSON.parse(
      filtersFromLocalStorage
    );

    return parsedFilters.map((filter) => {
      return {
        ...filter,
        period: filter.period
          ? [
              formatStringDateToDayjs(filter.period[0]),
              formatStringDateToDayjs(filter.period[1]),
            ]
          : null,
        birthdayPeriod: filter.birthdayPeriod
          ? [
              formatStringDateToDayjs(filter.birthdayPeriod[0]),
              formatStringDateToDayjs(filter.birthdayPeriod[1]),
            ]
          : null,
      };
    });
  }

  return [{ ...INITIAL_FILTERS }];
};

export const saveFiltersToLocalStorage = (filters: NewClientsListFilters[]) => {
  const filtersForStorage: NewClientsListFiltersForLocalStorage[] = filters.map(
    (filter) => {
      const { period, birthdayPeriod, ...rest } = filter;

      return {
        ...rest,
        period: period
          ? [formatDayjsToString(period[0]), formatDayjsToString(period[1])]
          : null,
        birthdayPeriod: birthdayPeriod
          ? [
              formatDayjsToString(birthdayPeriod[0]),
              formatDayjsToString(birthdayPeriod[1]),
            ]
          : null,
      };
    }
  );

  sessionStorage.setItem(
    'NewClientsListFilters',
    JSON.stringify(filtersForStorage)
  );
};

const lostPeriodStart = dayjs().subtract(60, 'day').startOf('day');
const lostPeriodEnd = dayjs().endOf('day');
const beforeLostPeriodEnd = lostPeriodStart.subtract(1, 'day').endOf('day');

export const CLIENTS_FILTER_SEGMENTS: Segment[] = [
  {
    label: 'Новые',
    value: 'new',
    tooltip: 'Новые клиенты, которые пришли к вам за последний месяц',
    filters: [
      {
        ...INITIAL_FILTERS,
        id: 1,
        type: 'and',
        hasRecords: true,
        recordsStatuses: [ClientStatusEnum.VISITED],
        recordsCountRange: { from: 1, to: 1 },
        period: [
          dayjs().subtract(1, 'month').startOf('day'),
          dayjs().endOf('day'),
        ],
      },
      {
        ...INITIAL_FILTERS,
        id: 2,
        type: 'and',
        hasRecords: false,
        recordsStatuses: [],
        recordsCountRange: { from: null, to: null },
        period: [
          dayjs().subtract(10, 'year').startOf('day'),
          dayjs().subtract(1, 'month').subtract(1, 'day').endOf('day'),
        ],
      },
    ],
  },
  {
    label: 'Лиды',
    value: 'leads',
    tooltip: 'Клиенты, у которых нет записей',
    filters: [
      {
        ...INITIAL_FILTERS,
        hasRecords: false,
        recordsStatuses: [],
        recordsCountRange: { from: null, to: null },
        period: null,
      },
    ],
  },
  {
    label: 'Повторные',
    value: 'repeated',
    tooltip: 'Клиенты, которые совершили повторный визит за последний месяц',
    filters: [
      {
        ...INITIAL_FILTERS,
        id: 1,
        type: 'and',
        hasRecords: true,
        recordsStatuses: [ClientStatusEnum.VISITED],
        recordsCountRange: { from: 1, to: null },
        period: [
          dayjs().subtract(1, 'month').startOf('day'),
          dayjs().endOf('day'),
        ],
      },
      {
        ...INITIAL_FILTERS,
        id: 2,
        type: 'and',
        hasRecords: true,
        recordsStatuses: [ClientStatusEnum.VISITED],
        recordsCountRange: { from: 1, to: null },
        period: [
          dayjs().subtract(10, 'year').startOf('day'),
          dayjs().subtract(1, 'month').subtract(1, 'day').endOf('day'),
        ],
      },
    ],
  },
  {
    label: 'Потерянные',
    value: 'lost',
    tooltip: 'Клиенты без визитов на протяжении последних 60 дней',
    filters: [
      {
        ...INITIAL_FILTERS,
        id: 1,
        type: 'and',
        hasRecords: true,
        recordsStatuses: [ClientStatusEnum.VISITED],
        recordsCountRange: { from: 1, to: null },
        period: [
          dayjs().subtract(10, 'year').startOf('day'),
          beforeLostPeriodEnd,
        ],
      },
      {
        ...INITIAL_FILTERS,
        id: 2,
        type: 'and',
        hasRecords: false,
        recordsStatuses: [],
        recordsCountRange: { from: null, to: null },
        period: [lostPeriodStart, lostPeriodEnd],
      },
    ],
  },
];

export const HAS_RECORDS_RADIO_OPTIONS: HasRecordsItem[] = [
  { label: 'Есть записи', value: true },
  { label: 'Нет записей', value: false },
];

export const HAS_TASKS_RADIO_OPTIONS: HasRecordsItem[] = [
  { label: 'Есть задачи', value: true },
  { label: 'Нет задач', value: false },
];

export const HAS_LOYALTY_CARD_RADIO_OPTIONS: HasRecordsItem[] = [
  { label: 'Есть карта лояльности', value: true },
  { label: 'Нет карты лояльности', value: false },
];

export const HAS_TAGS_RADIO_OPTIONS: RecordsTagsItem[] = [
  { label: 'Имеет выбранные теги', value: true },
  { label: 'Не имеет выбранные теги', value: false },
];

export const RECORD_STATUSES: RecordsStatusItem[] = [
  {
    value: ClientStatusEnum.VISITED,
    label: ClientStatusLabelEnum.VISITED_LABEL,
  },
  {
    value: ClientStatusEnum.CONFIRMED,
    label: ClientStatusLabelEnum.CONFIRMED_LABEL,
  },
  {
    value: ClientStatusEnum.WAITING,
    label: ClientStatusLabelEnum.WAITING_LABEL,
  },
  {
    value: ClientStatusEnum.NOVISIT,
    label: ClientStatusLabelEnum.NOVISIT_LABEL,
  },
];

export const GENDERS_OPTIONS: GenderItem[] = [
  { label: 'Мужской', value: 'm' },
  { label: 'Женский', value: 'f' },
  { label: 'Неизвестно', value: '' },
];

export const FILTER_TYPE_MENU: MenuProps['items'] = [
  {
    key: 'and',
    label: 'Тип связи "И"',
  },
  {
    key: 'or',
    label: 'Тип связи "ИЛИ"',
  },
];
